/**
 * API connectors on an agent — the editor's side of
 * /chatbots/<id>/api-connectors. Edit rights on the agent are required for
 * every call (a chat-only viewer gets 403 and never sees the list).
 */

import api from '@services/api';
import type { ConnectorLibraryStatus } from '@modules/connector_library/services/connectorLibraryService';

export type ApiAuthType = 'none' | 'api_key' | 'bearer' | 'basic';

export interface ApiOperationParameter {
  name: string;
  location: 'path' | 'query' | 'header' | 'cookie' | 'body';
  required: boolean;
  description: string;
  type: string | null;
}

/** What the editor decided for one operation. Absent = off. */
export interface ApiOperationSettings {
  enabled?: boolean;
  /** Response paths the model sees, e.g. `ref`, `versions[].text`, `[].name`. */
  fields?: string[];
  /** Parameters the editor fixed; the model never sees them. */
  pinned?: Record<string, unknown>;
  /** Parameters prefilled when the model leaves them out. */
  defaults?: Record<string, unknown>;
  max_calls_per_turn?: number;
  cache_ttl_seconds?: number;
  /** Response paths whose values are citable identifiers (verified citations). */
  verifies?: string[];
  description?: string;
}

export interface ApiOperation {
  operation_id: string;
  method: string;
  path: string;
  summary: string;
  description: string;
  parameters: ApiOperationParameter[];
  settings: ApiOperationSettings;
  /** Whether it can change data in the service (anything but GET / HEAD; switching it on asks first — item 69). */
  writes?: boolean;
}

export interface ApiConnector {
  connector_id: string;
  chatbot_id: string;
  name: string;
  slug: string;
  base_url: string;
  spec_url: string | null;
  spec_title: string | null;
  spec_version: string | null;
  operations: ApiOperation[];
  instructions: string;
  auth_type: ApiAuthType;
  has_auth: boolean;
  cache_ttl_seconds: number;
  max_result_chars: number;
  enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
  /** The entry of the organization's connector list this came from (null when added by hand). */
  library_entry_id?: string | null;
  library_version?: number | null;
  /** following / customized / detached; null when added by hand or before the list. */
  library_state?: 'following' | 'customized' | 'detached' | null;
  library_notice?: string | null;
  /** The list status (status chip, notice, key); null when not from the list. */
  library?: ConnectorLibraryStatus | null;
}

export interface ApiAuthInput {
  name?: string;
  in?: 'header' | 'query';
  value?: string;
  username?: string;
  password?: string;
}

export interface ReadSpecResult {
  title: string | null;
  version: string | null;
  base_url: string | null;
  operations: Omit<ApiOperation, 'settings'>[];
}

export interface CreateApiConnectorPayload {
  name: string;
  base_url?: string;
  spec_text?: string;
  spec_url?: string;
  operation_settings: Record<string, ApiOperationSettings>;
  instructions?: string;
  auth_type?: ApiAuthType;
  auth?: ApiAuthInput | null;
  cache_ttl_seconds?: number;
  max_result_chars?: number;
  /** The person's acknowledgement of a connector added by hand (required). */
  acknowledge_risk?: boolean;
}

export interface UpdateApiConnectorPayload {
  name?: string;
  enabled?: boolean;
  base_url?: string;
  operation_settings?: Record<string, ApiOperationSettings>;
  instructions?: string;
  auth_type?: ApiAuthType;
  /** A dict replaces the stored secret; null clears it; omitted keeps it. */
  auth?: ApiAuthInput | null;
  cache_ttl_seconds?: number;
  max_result_chars?: number;
  /** Required when the change switches on operations that change data. */
  acknowledge_write?: boolean;
}

export interface TryResult {
  request: { method: string; url: string; params?: [string, string][] };
  status: number;
  ok: boolean;
  /** Error kind on failure: not_found | invalid_request | auth | rate_limited | unavailable | too_large. */
  kind: string | null;
  /** The platform's automatic retry was used. */
  retried: boolean;
  result: string;
  truncated: boolean;
  raw_chars: number;
  shaped_chars: number;
  verified: string[];
}

/** The backend's error body: `reason`/`field` are machine keys the UI phrases. */
export interface ApiConnectorErrorBody {
  error?: string;
  reason?: string;
  field?: string;
}

const base = (chatbotId: string) => `/chatbots/${chatbotId}/api-connectors`;

class ApiConnectorService {
  async readSpec(chatbotId: string, input: { spec_text?: string; spec_url?: string }): Promise<ReadSpecResult> {
    const { data } = await api.post(`${base(chatbotId)}/read-spec`, input);
    return data;
  }

  async list(chatbotId: string): Promise<ApiConnector[]> {
    const { data } = await api.get(base(chatbotId));
    return data.connectors || [];
  }

  async create(chatbotId: string, payload: CreateApiConnectorPayload): Promise<ApiConnector> {
    const { data } = await api.post(base(chatbotId), payload);
    return data.connector;
  }

  async update(chatbotId: string, connectorId: string, payload: UpdateApiConnectorPayload): Promise<ApiConnector> {
    const { data } = await api.patch(`${base(chatbotId)}/${connectorId}`, payload);
    return data.connector;
  }

  async refresh(chatbotId: string, connectorId: string, specText?: string): Promise<ApiConnector> {
    const { data } = await api.post(`${base(chatbotId)}/${connectorId}/refresh`, specText ? { spec_text: specText } : {});
    return data.connector;
  }

  async tryOperation(chatbotId: string, connectorId: string, operationId: string, args: Record<string, unknown>): Promise<TryResult> {
    const { data } = await api.post(`${base(chatbotId)}/${connectorId}/try`, { operation_id: operationId, args });
    return data;
  }

  async remove(chatbotId: string, connectorId: string): Promise<void> {
    await api.delete(`${base(chatbotId)}/${connectorId}`);
  }
}

export const apiConnectorService = new ApiConnectorService();
